import { getErrorMessage } from "../agent/errors.js";
import process from "node:process";
import type { SessionStoreLike } from "../session/index.js";
import { runHostTurn } from "../host/turn.js";
import type { HostTurnRunner } from "../host/types.js";
import type { PromptRuntimeState } from "../agent/prompt/types.js";
import type { RegisteredTool, ToolFilter } from "../tools/core/types.js";
import type { RuntimeConfig, SessionRecord } from "../types.js";
import { defaultInteractiveExitGuard, type InteractiveExitGuard, type InteractiveExitProcess } from "./exitGuard.js";
import { handleLocalCommand, type LocalCommandResult } from "./localCommands.js";
import type { InteractionShell } from "./shell.js";

export interface InteractiveTurnContext {
  cwd?: string;
  stateRootDir?: string;
  builtinToolFilter?: ToolFilter;
  extraTools?: readonly RegisteredTool[];
  runtimePromptState?: Partial<PromptRuntimeState>;
}

export interface InteractiveSessionDriverOptions {
  cwd: string;
  config: RuntimeConfig;
  session: SessionRecord;
  sessionStore: SessionStoreLike;
  shell: InteractionShell;
  exitGuard?: InteractiveExitGuard;
  runTurn?: HostTurnRunner;
  localCommandHandler?: typeof handleLocalCommand;
  turnContextProvider?: (session: SessionRecord, input: string) => Promise<InteractiveTurnContext>;
  onSessionUpdated?: (session: SessionRecord) => void;
}

export class InteractiveSessionDriver {
  private session: SessionRecord;
  private turnInFlight = false;
  private turnAbortController: AbortController | null = null;
  private lastInterruptNoticeAt = 0;
  private exitRequested = false;
  private terminationInProgress = false;

  constructor(private readonly options: InteractiveSessionDriverOptions) {
    this.session = options.session;
  }

  async run(): Promise<SessionRecord> {
    const releaseInterrupt = this.options.shell.input.bindInterrupt(() => {
      this.handleInterrupt();
    });
    const releaseProcessTermination = this.bindProcessTerminationCleanup();

    try {
      while (true) {
        const prompt = await this.options.shell.input.readInput("> ");
        if (prompt.kind === "closed") {
          await this.terminateRunningProcessesForForcedExit("Input closed. Stopping running processes before exit.");
          return this.session;
        }

        const input = prompt.value.trim();
        if (!input) {
          continue;
        }

        const decision = await this.handleInput(input);
        if (decision === "quit") {
          return this.session;
        }
        if (this.exitRequested) {
          return this.session;
        }
      }
    } finally {
      releaseProcessTermination();
      releaseInterrupt();
    }
  }

  private async handleInput(input: string): Promise<LocalCommandResult> {
    let localCommandResult: LocalCommandResult;
    try {
      localCommandResult = await (this.options.localCommandHandler ?? handleLocalCommand)(
        input,
        {
          cwd: this.options.cwd,
          session: this.session,
          config: this.options.config,
          sessionStore: this.options.sessionStore,
        },
        this.options.shell.output,
      );
    } catch (error) {
      this.options.shell.output.error(getErrorMessage(error));
      return "handled";
    }

    if (localCommandResult === "continue") {
      await this.runTurn(input);
    } else if (localCommandResult === "quit") {
      return this.handleQuitRequest();
    }

    return localCommandResult;
  }

  private async handleQuitRequest(): Promise<LocalCommandResult> {
    const exitGuard = this.options.exitGuard ?? defaultInteractiveExitGuard;

    let runningProcesses: InteractiveExitProcess[];
    try {
      runningProcesses = await exitGuard.collectRunningProcesses(this.options.cwd);
    } catch (error) {
      this.options.shell.output.error(`Failed to inspect running background processes: ${getErrorMessage(error)}`);
      return "handled";
    }

    if (runningProcesses.length === 0) {
      this.options.shell.output.info("Session saved.");
      return "quit";
    }

    this.options.shell.output.warn("Running processes detected. Exiting now will kill them all.");
    this.options.shell.output.plain(runningProcesses.map((process) => process.summary).join("\n"));

    const confirmation = await this.options.shell.input.readInput(
      "Kill all running processes and exit? [y/N] ",
    );

    if (confirmation.kind !== "submit" || !isYes(confirmation.value)) {
      this.options.shell.output.info("Exit cancelled. Background processes will keep running.");
      return "handled";
    }

    try {
      const result = await exitGuard.terminateProcesses(runningProcesses, this.options.cwd);
      if (result.failedPids.length > 0) {
        this.options.shell.output.error(
          `Could not stop all background processes. Still running: ${result.failedPids.join(", ")}. Exit cancelled.`,
        );
        return "handled";
      }

      this.options.shell.output.warn(`Stopped ${result.terminatedPids.length} background process(es).`);
      this.options.shell.output.info("Session saved.");
      return "quit";
    } catch (error) {
      this.options.shell.output.error(`Failed to stop background processes: ${getErrorMessage(error)}`);
      return "handled";
    }
  }

  private handleInterrupt(): void {
    if (this.turnInFlight && this.turnAbortController && !this.turnAbortController.signal.aborted) {
      this.turnAbortController.abort();
      this.showInterruptNotice("Interrupted the current turn. You can continue typing.");
      return;
    }

    this.showInterruptNotice("This session will not exit automatically. Type quit or q to exit.");
  }

  private showInterruptNotice(message: string): void {
    const now = Date.now();
    if (now - this.lastInterruptNoticeAt < 150) {
      return;
    }

    this.lastInterruptNoticeAt = now;
    this.options.shell.output.interrupt(message);
  }

  private bindProcessTerminationCleanup(): () => void {
    const signals: NodeJS.Signals[] = ["SIGHUP", "SIGTERM", "SIGBREAK"];
    const handler = (signal: NodeJS.Signals): void => {
      void this.terminateRunningProcessesForForcedExit(
        `Received ${signal}. Stopping running processes before exit.`,
      ).finally(() => {
        process.exit(0);
      });
    };

    for (const signal of signals) {
      process.once(signal, handler);
    }

    return () => {
      for (const signal of signals) {
        process.off(signal, handler);
      }
    };
  }

  private async terminateRunningProcessesForForcedExit(reason: string): Promise<void> {
    if (this.terminationInProgress) {
      return;
    }

    this.terminationInProgress = true;
    this.exitRequested = true;
    if (this.turnAbortController && !this.turnAbortController.signal.aborted) {
      this.turnAbortController.abort();
    }

    const exitGuard = this.options.exitGuard ?? defaultInteractiveExitGuard;
    try {
      const runningProcesses = await exitGuard.collectRunningProcesses(this.options.cwd);
      if (runningProcesses.length === 0) {
        this.options.shell.output.info("Session saved.");
        return;
      }

      this.options.shell.output.warn(reason);
      this.options.shell.output.plain(runningProcesses.map((processInfo) => processInfo.summary).join("\n"));
      const result = await exitGuard.terminateProcesses(runningProcesses, this.options.cwd);
      if (result.failedPids.length > 0) {
        this.options.shell.output.error(`Could not stop all running processes. Still running: ${result.failedPids.join(", ")}.`);
        return;
      }

      this.options.shell.output.warn(`Stopped ${result.terminatedPids.length} running process(es).`);
      this.options.shell.output.info("Session saved.");
    } catch (error) {
      this.options.shell.output.error(`Failed to stop running processes: ${getErrorMessage(error)}`);
    }
  }

  private async runTurn(input: string): Promise<void> {
    this.options.shell.output.plain(formatSubmittedInput(input));
    this.turnInFlight = true;
    const controller = new AbortController();
    this.turnAbortController = controller;
    const turnDisplay = this.options.shell.createTurnDisplay({
      cwd: this.options.cwd,
      config: this.options.config,
      abortSignal: controller.signal,
    });

    try {
      const turnContext = await this.options.turnContextProvider?.(this.session, input);
      const outcome = await runHostTurn({
        host: "interactive",
        input,
        cwd: turnContext?.cwd ?? this.options.cwd,
        stateRootDir: turnContext?.stateRootDir,
        config: this.options.config,
        session: this.session,
        sessionStore: this.options.sessionStore,
        builtinToolFilter: turnContext?.builtinToolFilter,
        extraTools: turnContext?.extraTools,
        runtimePromptState: turnContext?.runtimePromptState,
        abortSignal: controller.signal,
        callbacks: turnDisplay.callbacks,
      }, {
        runTurn: this.options.runTurn,
      });

      this.session = outcome.session;
      this.options.onSessionUpdated?.(this.session);
      if (outcome.status === "aborted") {
        turnDisplay.flush();
        this.options.shell.output.warn(outcome.errorMessage ?? "Turn interrupted. You can keep chatting.");
        return;
      }

      if (outcome.status === "failed") {
        turnDisplay.flush();
        this.options.shell.output.error(outcome.errorMessage ?? "The request failed.");
        this.options.shell.output.info("The request failed, but the session is still alive. You can keep chatting.");
      }
    } catch (error) {
      turnDisplay.flush();
      this.options.shell.output.error(getErrorMessage(error));
      this.options.shell.output.info("The request failed, but the session is still alive. You can keep chatting.");
    } finally {
      turnDisplay.dispose();
      this.turnInFlight = false;
      this.turnAbortController = null;
    }
  }
}

function isYes(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized === "y" || normalized === "yes";
}

function formatSubmittedInput(input: string): string {
  return input
    .split("\n")
    .map((line, index) => `${index === 0 ? "> " : "… "}${line}`)
    .join("\n");
}
